/**
 * Vue Router Configuration - Laumeris
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores'

// Auth Pages
import LoginPage from '../pages/auth/LoginPage.vue'
import RegisterPage from '../pages/auth/RegisterPage.vue'
import ForgotPasswordPage from '../pages/auth/ForgotPasswordPage.vue'
import ResetPasswordPage from '../pages/auth/ResetPasswordPage.vue'
import TwoFactorVerifyPage from '../pages/auth/TwoFactorVerifyPage.vue'

// Route definitions
const routes: RouteRecordRaw[] = [
  // Auth routes (public)
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: { requiresGuest: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterPage,
    meta: { requiresGuest: true }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPasswordPage,
    meta: { requiresGuest: true }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPasswordPage,
    meta: { requiresGuest: true }
  },
  {
    path: '/2fa-verify',
    name: 'TwoFactorVerify',
    component: TwoFactorVerifyPage,
    meta: { requiresGuest: true }
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: () => import('../pages/auth/VerifyEmailPage.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/emergency-reset',
    name: 'EmergencyReset',
    component: () => import('../pages/auth/EmergencyResetPage.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/delete-account',
    name: 'DeleteAccount',
    component: () => import('../pages/auth/DeleteAccountPage.vue'),
    meta: { requiresGuest: true }
  },

  // Email Verification Required (authenticated but email not verified)
  {
    path: '/verify-email-required',
    name: 'VerifyEmailRequired',
    component: () => import('../pages/auth/EmailVerificationRequiredPage.vue'),
    meta: { requiresAuth: true, skipEmailCheck: true, skipPasswordCheck: true, skipProfileCheck: true }
  },

  // Force Password Change (first-time login)
  {
    path: '/force-password-change',
    name: 'ForcePasswordChange',
    component: () => import('../pages/auth/ForcePasswordChangePage.vue'),
    meta: { requiresAuth: true, skipPasswordCheck: true, skipProfileCheck: true }
  },

  // Complete Profile (first-time login)
  {
    path: '/complete-profile',
    name: 'CompleteProfile',
    component: () => import('../pages/profile/CompleteProfilePage.vue'),
    meta: { requiresAuth: true, skipPasswordCheck: true, skipProfileCheck: true }
  },

  // Dashboard (protected)
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../pages/dashboard/DashboardPage.vue'),
    meta: { requiresAuth: true }
  },

  // Clients
  {
    path: '/clients',
    name: 'Clients',
    component: () => import('../pages/clients/ClientsListPage.vue'),
    meta: { requiresAuth: true, permission: 'clients:read' }
  },
  {
    path: '/clients/:id/edit',
    name: 'ClientEdit',
    component: () => import('../pages/clients/ClientEditPage.vue'),
    meta: { requiresAuth: true, permission: 'clients:write' }
  },
  {
    path: '/clients/:id',
    name: 'ClientDetail',
    component: () => import('../pages/clients/ClientDetailPage.vue'),
    meta: { requiresAuth: true, permission: 'clients:read' }
  },

  // Documents
  {
    path: '/documents',
    name: 'Documents',
    component: () => import('../pages/documents/DocumentsListPage.vue'),
    meta: { requiresAuth: true, permission: 'documents:read' }
  },
  {
    path: '/documents/:id',
    name: 'DocumentDetail',
    component: () => import('../pages/documents/DocumentDetailPage.vue'),
    meta: { requiresAuth: true, permission: 'documents:read' }
  },

  // Invoices
  {
    path: '/invoices',
    name: 'Invoices',
    component: () => import('../pages/invoices/InvoicesListPage.vue'),
    meta: { requiresAuth: true, permission: 'invoices:read' }
  },
  {
    path: '/invoices/:id',
    name: 'InvoiceDetail',
    component: () => import('../pages/invoices/InvoiceDetailPage.vue'),
    meta: { requiresAuth: true, permission: 'invoices:read' }
  },
  {
    path: '/invoices/:id/pay',
    name: 'InvoicePayment',
    component: () => import('../pages/invoices/InvoicePaymentPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/invoices/:id/payment/success',
    name: 'InvoicePaymentSuccess',
    component: () => import('../pages/invoices/InvoicePaymentPage.vue'),
    meta: { requiresAuth: true }
  },

  // Quotes
  {
    path: '/quotes',
    name: 'Quotes',
    component: () => import('../pages/quotes/QuotesListPage.vue'),
    meta: { requiresAuth: true, permission: 'documents:read' }
  },
  {
    path: '/quotes/:id',
    name: 'QuoteDetail',
    component: () => import('../pages/quotes/QuoteDetailPage.vue'),
    meta: { requiresAuth: true, permission: 'documents:read' }
  },
  {
    path: '/quotes/:id/edit',
    name: 'QuoteEdit',
    component: () => import('../pages/quotes/QuoteEditPage.vue'),
    meta: { requiresAuth: true, permission: 'documents:write' }
  },

  // Tickets
  {
    path: '/tickets',
    name: 'Tickets',
    component: () => import('../pages/tickets/TicketsListPage.vue'),
    meta: { requiresAuth: true, permission: 'tickets:read' }
  },
  {
    path: '/tickets/:id',
    name: 'TicketDetail',
    component: () => import('../pages/tickets/TicketDetailPage.vue'),
    meta: { requiresAuth: true, permission: 'tickets:read' }
  },

  // Payments
  {
    path: '/payments',
    name: 'Payments',
    component: () => import('../pages/payments/PaymentsListPage.vue'),
    meta: { requiresAuth: true, permission: 'payments:read' }
  },

  // Analytics
  {
    path: '/analytics',
    name: 'Analytics',
    component: () => import('../pages/analytics/AnalyticsPage.vue'),
    meta: { requiresAuth: true, permission: 'analytics:read' }
  },

  // Team
  {
    path: '/team',
    name: 'Team',
    component: () => import('../pages/team/TeamListPage.vue'),
    meta: { requiresAuth: true, permission: 'team:read' }
  },

  // Registration Requests
  {
    path: '/requests',
    name: 'Requests',
    component: () => import('../pages/requests/RequestsListPage.vue'),
    meta: { requiresAuth: true, permission: 'team:read' }
  },

  // Settings
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('../pages/settings/SettingsPage.vue'),
    meta: { requiresAuth: true }
  },

  // Notifications
  {
    path: '/notifications',
    name: 'Notifications',
    component: () => import('../pages/notifications/NotificationsPage.vue'),
    meta: { requiresAuth: true }
  },

  // Profile
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('../pages/profile/ProfilePage.vue'),
    meta: { requiresAuth: true }
  },

  // Security
  {
    path: '/security',
    name: 'Security',
    component: () => import('../pages/security/SecurityPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/security/devices',
    name: 'Devices',
    component: () => import('../pages/security/DevicesPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/security/sessions',
    name: 'Sessions',
    component: () => import('../pages/security/SessionsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/security/audit',
    name: 'AuditLog',
    component: () => import('../pages/security/AuditLogPage.vue'),
    meta: { requiresAuth: true }
  },

  // Legal Pages (public)
  {
    path: '/legal/mentions-legales',
    name: 'MentionsLegales',
    component: () => import('../pages/legal/MentionsLegales.vue'),
    meta: { public: true }
  },
  {
    path: '/legal/confidentialite',
    name: 'Confidentialite',
    component: () => import('../pages/legal/Confidentialite.vue'),
    meta: { public: true }
  },
  {
    path: '/legal/cgu',
    name: 'CGU',
    component: () => import('../pages/legal/CGU.vue'),
    meta: { public: true }
  },
  {
    path: '/legal/cookies',
    name: 'Cookies',
    component: () => import('../pages/legal/Cookies.vue'),
    meta: { public: true }
  },
  // Aliases pour les URLs courtes
  {
    path: '/privacy',
    redirect: '/legal/confidentialite'
  },
  {
    path: '/terms',
    redirect: '/legal/cgu'
  },

  // 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../pages/errors/NotFoundPage.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Initialize auth store if not done
  if (!authStore.isInitialized) {
    authStore.initialize()
  }

  const requiresAuth = to.meta.requiresAuth as boolean
  const requiresGuest = to.meta.requiresGuest as boolean
  const requiredPermission = to.meta.permission as string | undefined
  const skipProfileCheck = to.meta.skipProfileCheck as boolean
  const skipPasswordCheck = to.meta.skipPasswordCheck as boolean
  const skipEmailCheck = to.meta.skipEmailCheck as boolean
  const isPublic = to.meta.public as boolean

  // Public routes
  if (isPublic) {
    return next()
  }

  // Guest routes (login, register, etc.)
  if (requiresGuest && authStore.isAuthenticated) {
    return next('/dashboard')
  }

  // Protected routes
  if (requiresAuth && !authStore.isAuthenticated) {
    return next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
  }

  // Fetch user data if authenticated but not loaded
  if (requiresAuth && authStore.isAuthenticated && !authStore.user) {
    await authStore.fetchUser()
  }

  // Check if email is verified (priority 1 - most important security check)
  // All users (including existing users) must verify their email
  if (requiresAuth && authStore.isAuthenticated && !skipEmailCheck) {
    const user = authStore.user
    if (user && !user.emailVerified) {
      if (to.path !== '/verify-email-required') {
        return next('/verify-email-required')
      }
    }
  }

  // Check if user must change password (first login with generated password)
  // This takes priority over profile completion
  if (requiresAuth && authStore.isAuthenticated && !skipPasswordCheck) {
    const user = authStore.user
    if (user?.mustChangePassword) {
      if (to.path !== '/force-password-change') {
        return next('/force-password-change')
      }
    }
  }

  // Check if profile needs completion (first-time login)
  // This flag is stored in the database, not localStorage
  if (requiresAuth && authStore.isAuthenticated && !skipProfileCheck) {
    const user = authStore.user
    // Check server-side flag for profile completion
    if (user?.needsProfileCompletion) {
      // Don't redirect owners or if already on complete-profile
      if (user.userType !== 'owner' && to.path !== '/complete-profile') {
        return next('/complete-profile')
      }
    }
  }

  // Permission-based access
  if (requiresAuth && requiredPermission) {
    // Clients have access to their own data (special handling)
    if (authStore.user?.userType === 'client') {
      const clientAllowedPaths = ['/dashboard', '/documents', '/invoices', '/quotes', '/tickets', '/profile', '/security', '/settings']
      const isClientAllowed = clientAllowedPaths.some(path => to.path.startsWith(path))
      if (!isClientAllowed) {
        return next('/dashboard')
      }
    } else if (!authStore.hasPermission(requiredPermission)) {
      return next('/dashboard')
    }
  }

  next()
})

export default router
